import { useCallback, useEffect, useRef, useState } from "react";
import { searchTextGallery } from "@/services/GalleriesService";
import { GalleryI } from "@/interfaces/galleries/Galleries";
import { useUserContext } from "@/context/UserContext";
import { LogI } from "@/interfaces/Logs";
import { createLog } from "@/services/LogService";
import { Success } from "@/utils/types/res/searchCollection/searchCollection";

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 500;
// Distance from the bottom of the results list that triggers the next page
const SCROLL_THRESHOLD = "80px";

export const useGallerySearch = () => {
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText, setDebouncedText] = useState<string>("");
  const [galleries, setGalleries] = useState<Array<GalleryI>>([]);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const { userToken } = useUserContext();
  const { Search_Success } = Success;

  // Ignores responses from searches that were superseded by a newer one
  const requestId = useRef(0);

  const isSearching = debouncedText.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    // A space means the user finished a word: search right away so whole-word
    // matches jump to the top without waiting for the debounce
    if (/\s$/.test(searchText)) {
      setDebouncedText(searchText);
      return;
    }
    const timerId = setTimeout(() => {
      setDebouncedText(searchText);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchText]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoadingMore(false);

    if (!isSearching) {
      setGalleries([]);
      setHasMore(false);
      setLoadingSearch(false);
      return;
    }

    setLoadingSearch(true);
    searchTextGallery(debouncedText).then((res) => {
      if (id !== requestId.current) return;
      const log: LogI = {
        type: "Search",
        typeNumber: 2,
        visitStart: Date.now(),
        text: debouncedText,
      };
      createLog(userToken, log);
      if (res && res.status === Search_Success) {
        setGalleries(res.data);
        setHasMore(Boolean(res.hasMore));
      } else {
        setGalleries([]);
        setHasMore(false);
      }
      setLoadingSearch(false);
    });
  }, [debouncedText]);

  const loadMore = useCallback(() => {
    if (loadingSearch || loadingMore || !hasMore) return;
    const id = requestId.current;
    setLoadingMore(true);
    searchTextGallery(debouncedText, galleries.length).then((res) => {
      if (id !== requestId.current) return;
      if (res && res.status === Search_Success) {
        setGalleries((prev) => [...prev, ...res.data]);
        setHasMore(Boolean(res.hasMore));
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    });
  }, [loadingSearch, loadingMore, hasMore, debouncedText, galleries.length]);

  // Attach to an element at the end of the results list; when it scrolls into
  // view the next page is requested
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLElement | null) => {
      observer.current?.disconnect();
      if (!node) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) loadMore();
        },
        { rootMargin: SCROLL_THRESHOLD }
      );
      observer.current.observe(node);
    },
    [loadMore]
  );

  const resetSearch = () => {
    setSearchText("");
  };

  return {
    searchText,
    setSearchText,
    debouncedText,
    isSearching,
    galleries,
    loadingSearch,
    loadingMore,
    hasMore,
    loadMoreRef,
    resetSearch,
  };
};
